/**
 * Module-level store of the Stream Manager's activity feed, so the feed survives the panel being
 * closed and reopened. Fed from the studio runtime by `attachActivity`.
 */
import { createStore } from 'zustand/vanilla';
import type { Unsubscribe } from '../../../core/services';
import type { StudioApi } from '../../../engine/api';
import { deriveActivity, snapshotOf, type ActivityDraft, type ActivityEvent } from './activityModel';

export const ACTIVITY_LIMIT = 100;

export interface ActivityState {
  /** Newest first, at most ACTIVITY_LIMIT. */
  events: ActivityEvent[];
  /** Wall-clock time the stream last went live; null while offline. */
  liveSince: number | null;
  /** The channel slug the control plane reported for the stream key, once known. */
  channelSlug: string | null;
  push(events: readonly ActivityDraft[]): void;
  clear(): void;
  setLiveSince(at: number | null): void;
  setChannelSlug(slug: string | null): void;
}

let nextId = 1;

export const activityStore = createStore<ActivityState>((set, get) => ({
  events: [],
  liveSince: null,
  channelSlug: null,
  push(events) {
    if (events.length === 0) return;
    const numbered = events.map((event) => ({ ...event, id: nextId++ })).reverse();
    set({ events: [...numbered, ...get().events].slice(0, ACTIVITY_LIMIT) });
  },
  clear() {
    set({ events: [] });
  },
  setLiveSince(at) {
    if (get().liveSince !== at) set({ liveSince: at });
  },
  setChannelSlug(slug) {
    if (get().channelSlug !== slug) set({ channelSlug: slug });
  },
}));

/** Feeds runtime changes of `studio` into the activity store until the returned function is called. */
export function attachActivity(studio: Pick<StudioApi, 'runtime' | 'project'>, now: () => number = Date.now): Unsubscribe {
  const mode = () => studio.project.getState().project.output.streaming.mode;
  let prev = snapshotOf(studio.runtime.getState(), mode());
  const trackLive = (): void => {
    const store = activityStore.getState();
    if (prev.linkStatus === 'live' && store.liveSince === null) store.setLiveSince(now());
    else if (prev.linkStatus === 'idle' || prev.linkStatus === 'error') store.setLiveSince(null);
  };
  trackLive();
  return studio.runtime.subscribe((runtime) => {
    const next = snapshotOf(runtime, mode());
    activityStore.getState().push(deriveActivity(prev, next, now()));
    prev = next;
    trackLive();
  });
}
